import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { useGetUserQuery } from '@/api';
import { KycStatus } from '@/api/common.types';
import { useCheckStatus } from '@/api/queryHooks/useKycController';
import { getKycFailureText } from '@/utils/kycFailure';
import { KycReasonNotice } from '../../KycReasonNotice';
import { Button } from '../../buttons';
import styles from './VerifyYourIdentity.module.css';
import { Sumsub } from '../../Sumsub';
import { Spinner } from '../../Spinner';

interface VerifyYourIdentityProps {
  onComplete: () => void;
  isAttentionStepSkipped?: boolean;
  verificationId: string;
  onError?: () => void;
}

export const VerifyYourIdentity = ({
  onComplete,
  isAttentionStepSkipped = false,
  verificationId,
  onError,
}: VerifyYourIdentityProps) => {
  const [isStarted, setIsStarted] = useState(false);
  const [isInited, setIsInited] = useState(false);
  const { data: userData } = useGetUserQuery();
  // RNB-195: surface why verification is needed (the moderator's reason / duplicate-account copy).
  const { data: kycStatusData } = useCheckStatus();

  const isInitialVerification = useMemo(() => {
    return userData?.kycStatus === KycStatus.EMPTY;
  }, [userData?.kycStatus]);

  const reasonText = getKycFailureText(kycStatusData?.failureReason, kycStatusData?.failureMessage);
  const isBiometric = kycStatusData?.status === KycStatus.BIOMETRIC_REQUIRED;

  const renderDescription = () => {
    if (isInitialVerification) {
      return (
        <>
          RelayPay is registered with AUSTRAC and as part of the AML &amp; CTF guidelines we are required to perform an
          ID verification to our users. Please visit our{' '}
          <a
            className={styles.link}
            href="https://www.relaypay.io/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Use
          </a>{' '}
          and{' '}
          <a
            className={styles.link}
            href="https://www.relaypay.io/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>{' '}
          for more information.
        </>
      );
    }
    if (isBiometric) {
      return `In light of recent increases in financial services related scams, we require you to complete biometric
            verifications before your transaction.`;
    }
    return `We couldn't verify some of your details. Please resubmit the documents we've highlighted — you only need to
            redo those steps, not the whole process.`;
  };

  function start() {
    setIsStarted(true);
  }

  function onInit() {
    setIsInited(true);
  }

  useEffect(() => {
    setIsStarted(isAttentionStepSkipped);
  }, [isAttentionStepSkipped]);

  return (
    <div className={styles.container}>
      {isStarted && (
        <Sumsub onInit={onInit} onComplete={onComplete} verificationId={verificationId} onError={onError} />
      )}

      {isAttentionStepSkipped && !isInited && <Spinner />}

      {!isAttentionStepSkipped &&
        (isInited ? null : (
          <div className={styles.wrapper}>
            <Image src="/assets/verifyIdentity.svg" width={100} height={100} alt="Verification" />
            <h3 className={styles.title}>
              {isInitialVerification ? 'Verify Your Identity' : 'Re-verify Your Identity'}
            </h3>
            <KycReasonNotice text={reasonText} align="center" className={styles.reasonNotice} />
            <p className={styles.description}>{renderDescription()}</p>
            <Button size="large" className={styles.button} loading={isStarted && !isInited} onPress={start}>
              Start Verification
            </Button>
          </div>
        ))}
    </div>
  );
};
